import React, { useState, useEffect } from 'react';
import { useLead } from '../../contexts/LeadContext';
import { format } from 'date-fns';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSend, FiCopy, FiCheck, FiAlertCircle, FiMail, FiSave, FiTrash2, FiEdit } = FiIcons;

const AIAssistant = ({ lead, onClose, apiKey, aiSettings }) => {
  const [contentType, setContentType] = useState('email');
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [includeHistory, setIncludeHistory] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);

  const { fetchContacts, addContact, updateContact, deleteContact } = useLead();

  // Fetch company information
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      setLoadingCompanyInfo(true);
      try {
        const { data, error } = await supabase
          .from('company_info')
          .select('*')
          .limit(1)
          .single();
        
        if (error) throw error;
        setCompanyInfo(data);
      } catch (err) {
        console.error('Error fetching company info:', err);
      } finally {
        setLoadingCompanyInfo(false);
      }
    };
    
    fetchCompanyInfo();
  }, []);

  // Fetch contact history
  useEffect(() => {
    const loadContacts = async () => {
      setLoadingContacts(true);
      const result = await fetchContacts(lead.id);
      if (result.success) {
        setContacts(result.data);
      }
      setLoadingContacts(false);
    };
    
    loadContacts();
  }, [lead.id, fetchContacts]);

  useEffect(() => {
    setError('');
  }, [apiKey]);

  const formatContactHistory = () => {
    if (!contacts.length) return '';
    
    return contacts.map(contact => `
      ${format(new Date(contact.contact_date), 'MMM d, yyyy')} - ${contact.contact_type.toUpperCase()}
      ${contact.subject ? `Subject: ${contact.subject}\n` : ''}
      ${contact.content}
      ${contact.notes ? `Notes: ${contact.notes}\n` : ''}
      ---`
    ).join('\n');
  };

  const createGmailUrl = () => {
    let recipient = '';
    let subject = '';
    let body = '';
    
    switch (contentType) {
      case 'email':
        recipient = lead.email || '';
        subject = `RE: ${lead.service_requested || 'Electrical Services'}`;
        body = encodeURIComponent(generatedContent);
        break;
      case 'sms':
      case 'call_script':
        recipient = '';
        subject = `Notes for ${lead.name} - ${contentType === 'sms' ? 'SMS' : 'Call Script'}`;
        body = encodeURIComponent(`Lead: ${lead.name}\nPhone: ${lead.phone}\n\n${generatedContent}`);
        break;
      default:
        break;
    }
    
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${recipient}&su=${encodeURIComponent(subject)}&body=${body}`;
  };

  const openGmailCompose = () => {
    window.open(createGmailUrl(), '_blank');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const contactData = {
        contact_type: contentType,
        subject: `AI Generated ${contentType.charAt(0).toUpperCase() + contentType.slice(1)}`,
        content: generatedContent,
        notes: prompt || 'Generated using AI Assistant'
      };
      
      const result = await addContact(lead.id, contactData);
      if (result.success) {
        setContacts([result.data, ...contacts]);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setSavingContact(false);
    }
  };

  const handleEditContact = async (contact) => {
    setEditingContact({ ...contact, editing: true });
  };

  const handleUpdateContact = async () => {
    try {
      const result = await updateContact(editingContact.id, {
        subject: editingContact.subject,
        content: editingContact.content,
        notes: editingContact.notes
      });
      
      if (result.success) {
        setContacts(contacts.map(c => c.id === editingContact.id ? result.data : c));
        setEditingContact(null);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact record?')) {
      return;
    }
    
    try {
      const result = await deleteContact(contactId);
      if (result.success) {
        setContacts(contacts.filter(c => c.id !== contactId));
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const getSystemPrompt = (type) => {
    const companyName = companyInfo?.company_name || 'True Potential Electric';
    const ownerName = companyInfo?.owner_name || '';
    const companyAddress = companyInfo?.address ? `${companyInfo.address}, ${companyInfo.city || ''}, ${companyInfo.state || ''} ${companyInfo.postal_code || ''}` : '';
    const companyPhone = companyInfo?.phone || '';
    const companyEmail = companyInfo?.email || '';
    const companyWebsite = companyInfo?.website || '';
    
    const companyDetails = `
    Company Information:
    - Company Name: ${companyName}${ownerName ? ' (owned by ' + ownerName + ')' : ''}
    - Business Type: Professional electrical services company
    ${companyAddress ? '- Address: ' + companyAddress : ''}
    ${companyPhone ? '- Phone: ' + companyPhone : ''}
    ${companyEmail ? '- Email: ' + companyEmail : ''}
    ${companyWebsite ? '- Website: ' + companyWebsite : ''}
    `;
    
    const basePrompt = {
      email: `You are an AI assistant helping ${companyName}, a professional electrical services company${ownerName ? ' owned by ' + ownerName : ''}. Generate a professional email for lead outreach. 
      
      ${companyDetails}
      
      The email should be:
      - Professional and friendly
      - Specific to electrical services
      - Include a clear call-to-action
      - Be concise but informative
      - Reference the specific service they're interested in if provided
      - Include appropriate contact information from the company details above`,
      
      call_script: `You are an AI assistant helping ${companyName}${ownerName ? ' owned by ' + ownerName : ''} create call scripts for lead outreach. 
      
      ${companyDetails}
      
      Generate a professional phone call script that:
      - Starts with a professional introduction of ${ownerName || 'our company'}
      - References how we got their contact information
      - Discusses their electrical needs
      - Builds rapport and trust
      - Includes questions to qualify the lead
      - Ends with a clear next step
      - Includes appropriate contact information from the company details above`,
      
      sms: `You are an AI assistant helping ${companyName}${ownerName ? ' owned by ' + ownerName : ''} create SMS messages for lead outreach.
      
      ${companyDetails}
      
      Generate a professional but concise SMS that:
      - Is under 160 characters if possible
      - Is professional but friendly
      - Includes a clear call-to-action
      - References their electrical service needs
      - Includes appropriate contact information from the company details above`
    }[type] || `Generate professional content for electrical services lead outreach.
    
    ${companyDetails}`;
    
    if (includeHistory && contacts.length > 0) {
      return `${basePrompt}\n\nPrevious contact history with this lead:\n${formatContactHistory()}\n\nUse this history to provide context and continuity in the new message.`;
    }
    
    return basePrompt;
  };

  const generateContent = async () => {
    if (aiSettings.provider === 'openai' && !apiKey) {
      setError('OpenAI API key is required. Please set it in Settings.');
      return;
    }
    
    if (aiSettings.provider === 'straico' && !aiSettings.straico_api_key) {
      setError('Straico API key is required. Please set it in Settings.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const systemPrompt = getSystemPrompt(contentType);
      
      const userPrompt = `Lead Information:
      Name: ${lead.name}
      Phone: ${lead.phone}
      Email: ${lead.email || 'Not provided'}
      Company: ${lead.company_name || 'Not provided'}
      Service Requested: ${lead.service_requested || 'Not specified'}
      Lead Source: ${lead.lead_source || 'Not specified'}
      ${lead.ai_reference_notes ? 'AI Reference Notes: ' + lead.ai_reference_notes : ''}
      
      Additional Context: ${prompt}
      
      Please generate a professional ${contentType} for this lead.`;
      
      let response;
      
      if (aiSettings.provider === 'openai') {
        response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: aiSettings.openai_model || 'gpt-3.5-turbo',
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: userPrompt }
            ],
            max_tokens: 500,
            temperature: 0.7
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Failed to generate content');
        }
        
        const data = await response.json();
        setGeneratedContent(data.choices[0].message.content);
      } else if (aiSettings.provider === 'straico') {
        // Updated Straico API call based on the sample provided
        response = await fetch('https://api.straico.com/v1/prompt/completion', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${aiSettings.straico_api_key}`
          },
          body: JSON.stringify({
            models: [aiSettings.straico_model || 'openai/gpt-3.5-turbo-0125'],
            message: `${systemPrompt}\n\n${userPrompt}`
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Failed to generate content with Straico');
        }
        
        const data = await response.json();
        console.log("Straico API response:", data);
        
        if (!data.success) {
          throw new Error('Straico API returned unsuccessful response');
        }
        
        // Extract content based on the provided response structure
        const modelKey = Object.keys(data.data.completions)[0]; // Get the first model key
        const content = data.data.completions[modelKey]?.completion?.choices[0]?.message?.content;
        
        if (!content) {
          throw new Error('Could not find content in Straico API response');
        }
        
        setGeneratedContent(content);
      }
    } catch (error) {
      console.error('Error generating content:', error);
      setError(`Failed to generate content: ${error.message}. Please check your API key and try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">AI Assistant</h2>
              <p className="text-gray-600">Generate content for {lead.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Content Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type
              </label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
              >
                <option value="email">Email Draft</option>
                <option value="call_script">Call Script</option>
                <option value="sms">SMS Message</option>
              </select>
            </div>

            {/* Include History Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeHistory"
                checked={includeHistory}
                onChange={(e) => setIncludeHistory(e.target.checked)}
                className="h-4 w-4 text-electric-600 focus:ring-electric-500 border-gray-300 rounded"
              />
              <label htmlFor="includeHistory" className="ml-2 block text-sm text-gray-700">
                Include previous contact history as context
              </label>
            </div>

            {/* Additional Context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Context (Optional)
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                placeholder="e.g., This is a follow-up call, they mentioned urgency, they're a commercial client..."
              />
            </div>

            {/* API Key Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <p className="text-xs text-red-600 mt-1">
                    Please check that you've saved a valid API key in the Settings.
                  </p>
                </div>
              </div>
            )}

            {/* Company Info Display */}
            {companyInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-700 mb-1">Using Company Information:</h4>
                <p className="text-xs text-blue-600">
                  {companyInfo.company_name}{companyInfo.owner_name ? ` (${companyInfo.owner_name})` : ''} • 
                  {companyInfo.phone ? ` ${companyInfo.phone} • ` : ' '}
                  {companyInfo.email ? `${companyInfo.email}` : ''}
                </p>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={generateContent}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon icon={FiSend} className="h-5 w-5" />
              <span>{loading ? 'Generating...' : 'Generate Content'}</span>
            </button>

            {/* Generated Content */}
            {generatedContent && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Content</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={copyToClipboard}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="h-4 w-4" />
                      <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                    <button
                      onClick={openGmailCompose}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiMail} className="h-4 w-4" />
                      <span>Open in Gmail</span>
                    </button>
                    <button
                      onClick={handleSaveContact}
                      disabled={savingContact}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiSave} className="h-4 w-4" />
                      <span>{savingContact ? 'Saving...' : 'Save as Contact'}</span>
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                    {generatedContent}
                  </pre>
                </div>
              </div>
            )}

            {/* Contact History */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact History</h3>
              
              {loadingContacts ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-600"></div>
                </div>
              ) : contacts.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No contact history available</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map(contact => (
                    <div key={contact.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      {editingContact?.id === contact.id ? (
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={editingContact.subject}
                            onChange={(e) => setEditingContact({...editingContact, subject: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Subject"
                          />
                          <textarea
                            value={editingContact.content}
                            onChange={(e) => setEditingContact({...editingContact, content: e.target.value})}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                          <textarea
                            value={editingContact.notes}
                            onChange={(e) => setEditingContact({...editingContact, notes: e.target.value})}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Notes"
                          />
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingContact(null)}
                              className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateContact}
                              className="px-3 py-1 bg-electric-600 text-white rounded hover:bg-electric-700"
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                {format(new Date(contact.contact_date), 'MMM d, yyyy h:mm a')}
                              </span>
                              <h4 className="font-medium text-gray-900 mt-1">{contact.subject}</h4>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditContact(contact)}
                                className="p-1 text-gray-600 hover:text-gray-900"
                              >
                                <SafeIcon icon={FiEdit} className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                className="p-1 text-red-600 hover:text-red-700"
                              >
                                <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="mt-2 text-gray-700 whitespace-pre-wrap">
                            {contact.content}
                          </div>
                          
                          {contact.notes && (
                            <div className="mt-2 text-sm text-gray-600">
                              <strong>Notes:</strong> {contact.notes}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;